<script setup>
import { ref, computed, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import AgentBotModal from './components/AgentBotModal.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import { Badge } from 'dashboard/components-next/polaris/ui/badge';
import RowMenu from 'dashboard/components-next/polaris/cards/RowMenu.vue';
import { useSettingsRowMenu } from '../components/useSettingsRowMenu';

const MODAL_TYPES = {
  CREATE: 'create',
  EDIT: 'edit',
};

const store = useStore();
const { t } = useI18n();
const { rowMenuItems, rowMenuLabel } = useSettingsRowMenu();

const agentBots = useMapGetter('agentBots/getBots');
const uiFlags = useMapGetter('agentBots/getUIFlags');

const selectedBot = ref({});
const searchQuery = ref('');
const modalType = ref(MODAL_TYPES.CREATE);
const agentBotModalRef = ref(null);
const agentBotDeleteDialogRef = ref(null);

const tableHeaders = computed(() => {
  return [
    t('AGENT_BOTS.LIST.TABLE_HEADER.DETAILS'),
    t('AGENT_BOTS.LIST.TABLE_HEADER.URL'),
    t('AGENT_BOTS.LIST.TABLE_HEADER.ACTIONS'),
  ];
});

const selectedBotName = computed(() => selectedBot.value?.name || '');

const filteredAgentBots = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return agentBots.value;
  return picoSearch(agentBots.value, query, ['name', 'description']);
});

// The drawer that is open lets go first (and asks when it has changes).
const openAddModal = () => {
  agentBotModalRef.value.runGuarded(() => {
    modalType.value = MODAL_TYPES.CREATE;
    selectedBot.value = {};
    agentBotModalRef.value.dialogRef.open();
  });
};

const openEditModal = bot => {
  agentBotModalRef.value.runGuarded(() => {
    modalType.value = MODAL_TYPES.EDIT;
    selectedBot.value = bot;
    agentBotModalRef.value.dialogRef.open();
  });
};

const openDeletePopup = bot => {
  selectedBot.value = bot;
  agentBotDeleteDialogRef.value.open();
};

const deleteAgentBot = async id => {
  try {
    await store.dispatch('agentBots/delete', id);
    useAlert(t('AGENT_BOTS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_BOTS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    selectedBot.value = {};
  }
};

const onRowAction = (action, bot) => {
  if (action === 'edit') openEditModal(bot);
  else openDeletePopup(bot);
};

const confirmDeletion = () => {
  deleteAgentBot(selectedBot.value.id);
  agentBotDeleteDialogRef.value.close();
};

onMounted(() => {
  store.dispatch('agentBots/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="t('AGENT_BOTS.LIST.LOADING')"
    :no-records-found="!agentBots.length"
    :no-records-message="t('AGENT_BOTS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="t('AGENT_BOTS.HEADER')"
        :description="t('AGENT_BOTS.DESCRIPTION')"
        :link-text="t('AGENT_BOTS.LEARN_MORE')"
        :search-placeholder="t('AGENT_BOTS.SEARCH_PLACEHOLDER')"
        feature-name="agent_bots"
      >
        <template v-if="agentBots?.length" #count>
          {{ $t('AGENT_BOTS.COUNT', { n: agentBots.length }) }}
        </template>
        <template #actions>
          <Button
            :label="$t('AGENT_BOTS.ADD.TITLE')"
            size="sm"
            @click="openAddModal"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #drawer>
      <AgentBotModal
        ref="agentBotModalRef"
        :type="modalType"
        :selected-bot="selectedBot"
      />
    </template>
    <template #emptyActions>
      <Button
        :label="$t('AGENT_BOTS.ADD.TITLE')"
        size="sm"
        outline
        @click="openAddModal"
      />
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredAgentBots"
        :no-data-message="
          searchQuery ? t('AGENT_BOTS.NO_RESULTS') : t('AGENT_BOTS.LIST.404')
        "
        pin-last-column
        :clearable="!!searchQuery"
        @clear="searchQuery = ''"
      >
        <template #row="{ items }">
          <BaseTableRow v-for="bot in items" :key="bot.id" :item="bot">
            <BaseTableCell wrap>
              <div class="flex min-w-0 items-center gap-3">
                <Avatar
                  :name="bot.name"
                  :src="bot.thumbnail"
                  :size="32"
                  class="shrink-0"
                />
                <div class="grid min-w-0">
                  <span class="flex min-w-0 items-center gap-2">
                    <span class="truncate">{{ bot.name }}</span>
                    <Badge v-if="bot.system_bot" variant="secondary">
                      {{ $t('AGENT_BOTS.GLOBAL_BOT_BADGE') }}
                    </Badge>
                  </span>
                  <span
                    v-if="bot.description"
                    class="font-normal text-polaris-muted-foreground"
                  >
                    {{ bot.description }}
                  </span>
                </div>
              </div>
            </BaseTableCell>
            <BaseTableCell wrap class="break-all">
              {{ bot.outgoing_url || bot.bot_config?.webhook_url }}
            </BaseTableCell>
            <BaseTableCell actions>
              <RowMenu
                v-if="!bot.system_bot"
                :label="rowMenuLabel(bot.name)"
                :items="rowMenuItems()"
                @action="action => onRowAction(action, bot)"
              />
            </BaseTableCell>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <Dialog
      ref="agentBotDeleteDialogRef"
      type="alert"
      :title="t('AGENT_BOTS.DELETE.CONFIRM.TITLE')"
      :description="
        t('AGENT_BOTS.DELETE.CONFIRM.MESSAGE', { name: selectedBotName })
      "
      :is-loading="uiFlags.isDeleting"
      :confirm-button-label="t('AGENT_BOTS.DELETE.CONFIRM.YES')"
      :cancel-button-label="t('AGENT_BOTS.DELETE.CONFIRM.NO')"
      @confirm="confirmDeletion"
    />
  </SettingsLayout>
</template>
